import type { EntityReadStorage } from 'serviceWorker/channelServers/entitySource';
import { runUnlessRunningOrRecentlyEnded } from 'serviceWorker/helpers/runUnlessRunningOrRecentlyEnded';
import type { AnyChannelContract } from 'shared/channels/contract';
import type { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { SyncedEntity } from './createSyncedEntity';

type ThrottledReadStorageConfig = Readonly<{
  service: EntityReadStorage['service'];
  contract: Pick<AnyChannelContract, 'key'>;
  /** The event a sync publishes with the collection: it ends the wait for the first download. */
  collectionEvent: DomainMessagesTypes;
  synced: SyncedEntity;
  /** How long after the end of a refresh the reads do not start another one. */
  refreshIntervalMs: number;
}>;

/**
 * What reads of a synced entity need: they wait for its first download only
 * (a sync running meanwhile does not hold them) and refresh the entity from the
 * API afterwards, unless a refresh runs or ended less than `refreshIntervalMs`
 * ago. A view reads on every navigation and again when a sync ended; counted
 * from the end of the refresh, that read never starts another one, also when
 * the sync took longer than the interval (a slow network), so it cannot loop.
 */
export function createThrottledReadStorage({
  service,
  contract,
  collectionEvent,
  synced,
  refreshIntervalMs,
}: ThrottledReadStorageConfig): EntityReadStorage {
  return {
    service,
    contract: { key: contract.key, collectionEvent },
    ...synced,
    refreshAfterRead: runUnlessRunningOrRecentlyEnded(synced.syncInBackground, refreshIntervalMs),
  };
}

/** The same reads without the refresh: a background read of the page must not sync again. */
export function withoutRefreshAfterRead(
  storage: EntityReadStorage
): EntityReadStorage {
  return { ...storage, syncAfterRead: false };
}
